import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Divider, Flex, Form, Input, Modal, Steps, Typography } from "antd";
import Upload from "@/components/Upload";
import templateStore from "@/store/templateStore";
import { GetLocalStorage } from "@/utils/helper";
import PublicQuoteOfTheDay from "@/page/PublicTemplates/QuoteOfTheDay";
import type { ICusTemplate } from "@/store/interface/template";

const DAY_LABELS = ["Thứ Hai", "Thứ Ba", "Thứ Tư", "Thứ Năm", "Thứ Sáu", "Thứ Bảy", "Chủ Nhật"];
const DAY_EMOJIS = ["🌱", "🔥", "💧", "⚡", "🌟", "🎯", "☀️"];

const QuoteField = ({ dayIndex }: { dayIndex: number }) => (
  <div
    style={{
      background: "rgba(0,0,0,0.02)",
      borderRadius: 12,
      padding: "12px 16px",
      marginBottom: 12,
      border: "1px solid rgba(0,0,0,0.04)",
    }}
  >
    <Typography.Text strong style={{ fontSize: "0.85rem" }}>
      {DAY_EMOJIS[dayIndex]} {DAY_LABELS[dayIndex]}
    </Typography.Text>
    <Form.Item
      name={["quotes", dayIndex, "text"]}
      label="Câu trích dẫn"
      rules={[{ required: true, message: `Nhập quote cho ${DAY_LABELS[dayIndex]}` }]}
      style={{ marginBottom: 8, marginTop: 8 }}
    >
      <Input.TextArea placeholder={`Nhập câu trích dẫn cho ${DAY_LABELS[dayIndex]}...`} rows={2} />
    </Form.Item>
    <Form.Item name={["quotes", dayIndex, "author"]} label="Tác giả" style={{ marginBottom: 0 }}>
      <Input placeholder="Tên tác giả (tuỳ chọn)" />
    </Form.Item>
  </div>
);

const FormQuoteOfTheDay = ({ initialData }: { initialData?: ICusTemplate }) => {
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewConfig, setPreviewConfig] = useState<any>({});
  const { saveConfig, updateConfig } = templateStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (initialData) form.setFieldsValue(initialData.config);
  }, []);

  const handlePreview = () => {
    setPreviewConfig(form.getFieldsValue());
    setPreviewOpen(true);
  };

  const steps = [{ title: "Bước 1" }, { title: "Bước 2" }, { title: "Bước 3" }];

  const stepContent = [
    // Step 1 — Thông tin chung + Quote Thứ 2, 3, 4
    <>
      <Form.Item name="sender_name" label="Tên người chia sẻ">
        <Input placeholder="Nhập tên của bạn" />
      </Form.Item>
      <Form.Item name="sender_avatar" label="Ảnh đại diện">
        <Upload maxSize={10} />
      </Form.Item>
      <Form.Item name="category" label="Chủ đề chung">
        <Input placeholder="VD: Cuộc sống, Sự nghiệp, Tình yêu..." />
      </Form.Item>

      <Divider plain style={{ fontSize: "0.8rem", color: "rgba(0,0,0,0.4)" }}>
        📖 Câu trích dẫn Thứ 2 → 4
      </Divider>

      <QuoteField dayIndex={0} />
      <QuoteField dayIndex={1} />
      <QuoteField dayIndex={2} />
    </>,
    // Step 2 — Quote Thứ 5, 6, 7, CN
    <>
      <Divider plain style={{ fontSize: "0.8rem", color: "rgba(0,0,0,0.4)" }}>
        📖 Câu trích dẫn Thứ 5 → Chủ Nhật
      </Divider>

      <QuoteField dayIndex={3} />
      <QuoteField dayIndex={4} />
      <QuoteField dayIndex={5} />
      <QuoteField dayIndex={6} />
    </>,
    // Step 3 — Ảnh nền & Video
    <>
      <Form.Item name="background" label="Ảnh nền (tuỳ chọn)">
        <Upload maxSize={10} />
      </Form.Item>
      <Form.Item name="video" label="Video truyền cảm hứng (tuỳ chọn)">
        <Upload type="video" maxSize={10} />
      </Form.Item>
      <Form.Item name="list_image" label="Bộ ảnh minh hoạ (tuỳ chọn)">
        <Upload multiple maxSize={10} />
      </Form.Item>
    </>,
  ];

  const next = () => setCurrentStep(currentStep + 1);
  const prev = () => setCurrentStep(currentStep - 1);

  const handleFinish = async (value: any) => {
    try {
      let result;
      if (initialData) {
        result = await updateConfig(initialData.xid, { config: { ...value } });
      } else {
        const serial = await GetLocalStorage("scan-storage", "serial");
        result = await saveConfig({ type: "quote_of_the_day", chip_serial: serial, config: { ...value } });
      }
      if (result) navigate("/");
    } catch (error) {
      console.log("error", error);
    }
  };

  return (
    <section className="container">
      <Steps responsive={false} type="dot" titlePlacement="vertical" current={currentStep} items={steps} />

      <Form form={form} layout="vertical" style={{ marginTop: 24 }} onFinish={handleFinish} preserve>
        {stepContent.map((content, index) => (
          <div key={index} style={{ display: index === currentStep ? "block" : "none" }}>
            {content}
          </div>
        ))}

        <Flex className="mt-3">
          {currentStep > 0 && (
            <Button style={{ marginRight: 8 }} onClick={prev} className="w-full">
              Quay lại
            </Button>
          )}
          {currentStep < steps.length - 1 && (
            <Button type="primary" onClick={next} className="w-full">
              Tiếp theo
            </Button>
          )}
          {currentStep === steps.length - 1 && (
            <>
              <Button onClick={handlePreview} style={{ marginRight: 8 }} className="w-full">
                Xem trước
              </Button>
              <Button type="primary" htmlType="submit" className="w-full">
                Hoàn thành
              </Button>
            </>
          )}
        </Flex>
      </Form>

      <Modal
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        title="Xem trước"
        width={480}
        style={{ top: 8 }}
        styles={{ body: { padding: 0, height: "calc(100vh - 110px)", overflow: "auto" } }}
      >
        <PublicQuoteOfTheDay config={previewConfig} />
      </Modal>
    </section>
  );
};

export default FormQuoteOfTheDay;
